import { CarrierDto, KnownCarriersContainerType } from '@groundfloor-platform/mgmt-service-g-sdk';
import { Add } from '../../../../ui/components/pure/icons/Add.icon';
import { Edit } from '../../../../ui/components/pure/icons/Edit.icon';
import { Approval } from '../../../../ui/components/pure/icons/Approval.icon';
import { LinkIcon } from '../../../../ui/components/pure/icons/Link.icon';
import { ActionDelete } from '../../../../ui/components/pure/icons/ActionDelete.icon';
import { ActionsButtonItem } from '../../../../ui/components/pure/actions-button/actions-button.types';
import { makeDefineActions } from '../../../../ui/components/pure/actions-button/actions-button.helpers';
import { Permissions } from '../../../../core/permissions/permissions.typings';
import { canAddCourier, canDeleteCarrier, needsReview } from '../../logic/carrier-review';

export enum CarrierDetailsActions {
  EDIT_CARRIER = 'EDIT_CARRIER',
  EDIT_POLICIES = 'EDIT_POLICIES',
  EDIT_SUCCESS = 'EDIT_SUCCESS',
  APPROVE_CARRIER = 'APPROVE_CARRIER',
  APPROVE_SUCCESS = 'APPROVE_SUCCESS',
  DELETE_CARRIER = 'DELETE_CARRIER',
  ADD_NOTE = 'ADD_NOTE',
  ADD_NOTE_SUCCESS = 'ADD_NOTE_SUCCESS',
  ADD_COURIER = 'ADD_COURIER',
  ADD_COURIER_SUCCESS = 'ADD_COURIER_SUCCESS',
  ASSIGN_NETWORKS = 'ASSIGN_NETWORKS'
}

/** `groups:*`, as the API authorises a carrier's own edits as a group's. */
function makeCarrierDetailsActions(projectID: string): ActionsButtonItem<CarrierDetailsActions>[] {
  return [
    {
      title: 'Add courier',
      Icon: Add,
      action: CarrierDetailsActions.ADD_COURIER,
      permission: { action: Permissions.users.create, projectID }
    },
    {
      title: 'Edit carrier',
      Icon: Edit,
      action: CarrierDetailsActions.EDIT_CARRIER,
      permission: { action: Permissions.groups.update, projectID }
    },
    {
      title: 'Edit policies',
      Icon: Approval,
      action: CarrierDetailsActions.EDIT_POLICIES,
      permission: { action: Permissions.groups.update, projectID },
      availableUiFeatures: ['experimental']
    },
    {
      title: 'Assign networks',
      Icon: LinkIcon,
      action: CarrierDetailsActions.ASSIGN_NETWORKS,
      permission: { action: Permissions.groups.update, projectID },
      availableUiFeatures: ['experimental']
    },
    {
      title: 'Approve carrier',
      Icon: Approval,
      action: CarrierDetailsActions.APPROVE_CARRIER,
      permission: { action: Permissions.groups.update, projectID }
    },
    {
      dividerBefore: true,
      title: 'Delete carrier',
      Icon: ActionDelete,
      action: CarrierDetailsActions.DELETE_CARRIER,
      permission: { action: Permissions.groups.delete, projectID }
    }
  ];
}

export function makeValidateCarrierAction(carrier: Pick<CarrierDto, 'isFlagged' | 'status' | 'container'>) {
  return function validateAction(action: CarrierDetailsActions) {
    switch (action) {
      case CarrierDetailsActions.ADD_COURIER:
        return canAddCourier(carrier);
      // A company's carriers work through their organisation's networks, assigned in Settings.
      case CarrierDetailsActions.ASSIGN_NETWORKS:
        return carrier.container?.containerType === KnownCarriersContainerType.NetworkPool;
      case CarrierDetailsActions.APPROVE_CARRIER:
        return needsReview(carrier);
      case CarrierDetailsActions.DELETE_CARRIER:
        return canDeleteCarrier(carrier);
      default:
        return true;
    }
  };
}

/** `projectID` is the one the API authorises the carrier's actions against — see `carrierAuthorisingProjectID`. */
export function makeDefineCarrierDetailsActions(carrier: CarrierDto, projectID: string) {
  return makeDefineActions(makeCarrierDetailsActions(projectID), undefined, makeValidateCarrierAction(carrier));
}
